import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FilePlus, Sparkles, Zap, User, Receipt, LogOut, Crown, MapPin, Check, Lock, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useEstimationForm } from '../../contexts/EstimationFormContext';
import { DETAILED_STEPS } from '../estimation/detailed/config';

const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Mes estimations' },
  { to: '/estimation/nouvelle', icon: FilePlus, label: 'Estimation détaillée' },
  { to: '/quick-estimate', icon: Zap, label: 'Estimation rapide' },
];

const AI_NAV_ITEM = { to: '/estimation/assistant', label: 'SimulReno AI' };

const ACCOUNT_ITEMS = [
  { to: '/profile', icon: User, label: 'Profil' },
  { to: '/billing', icon: Receipt, label: 'Facturation' },
];

const WavePattern = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: 0.06, zIndex: 0 }}
    preserveAspectRatio="xMidYMid slice"
  >
    <defs>
      <pattern id="waves" x="0" y="0" width="280" height="200" patternUnits="userSpaceOnUse">
        <path d="M-40,80 C20,40 80,120 140,80 S260,40 320,80" fill="none" stroke="white" strokeWidth="1.5" />
        <path d="M-40,120 C20,80 80,160 140,120 S260,80 320,120" fill="none" stroke="white" strokeWidth="1.5" />
        <path d="M-40,160 C20,120 80,200 140,160 S260,120 320,160" fill="none" stroke="white" strokeWidth="1.5" />
        <path d="M-40,40 C20,0 80,80 140,40 S260,0 320,40" fill="none" stroke="white" strokeWidth="1" />
        <path d="M-40,200 C20,160 80,240 140,200 S260,160 320,200" fill="none" stroke="white" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#waves)" />
  </svg>
);

interface NavItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
  active: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon: Icon, label, active }) => (
  <Link
    to={to}
    className="flex items-center gap-3 px-3 py-2 text-sm transition-colors"
    style={{
      borderRadius: 'var(--radius-sm)',
      color: active ? '#ffffff' : 'rgba(255,255,255,0.55)',
      background: active ? 'rgba(255,255,255,0.13)' : 'transparent',
      border: active ? '0.5px solid rgba(255,255,255,0.15)' : '0.5px solid transparent',
    }}
  >
    <Icon size={16} strokeWidth={1.75} />
    {label}
  </Link>
);

const Sidebar: React.FC = () => {
  const { user, signOut } = useAuth();
  const { profile } = useUserProfile(user?.uid);
  const location = useLocation();
  const estimationForm = useEstimationForm();

  const isEstimationActive = estimationForm?.isActive ?? false;
  const currentStep = estimationForm?.currentStep ?? '';
  const completedSteps = estimationForm?.completedSteps ?? [];
  const formAddress = estimationForm?.formAddress ?? '';
  const formPostcode = estimationForm?.formPostcode ?? '';
  const formCity = estimationForm?.formCity ?? '';
  const isEditMode = estimationForm?.isEditMode ?? false;
  const aiSteps = estimationForm?.aiSteps ?? [];
  const onStepClickRef = estimationForm?.onStepClickRef;

  const isActive = (to: string) => {
    if (to === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(to);
  };

  const isUnlimited =
    profile?.subscriptionStatus === 'active' ||
    profile?.subscriptionStatus === 'trialing';
  const credits = profile?.credits ?? 0;
  const maxCreditsForBar = 10;
  const barPercent = isUnlimited ? 100 : Math.min((credits / maxCreditsForBar) * 100, 100);

  return (
    <aside
      className="relative flex flex-col h-full overflow-hidden shrink-0"
      style={{ width: 240, background: 'var(--gradient-sidebar)' }}
    >
      <WavePattern />

      <div className="relative flex flex-col h-full" style={{ zIndex: 1 }}>
        {/* User identity — hidden in estimation mode */}
        {!isEstimationActive && (
          <>
            <div className="flex items-center gap-3 px-5 pt-6 pb-5">
              <div
                style={{
                  width: 36, height: 36, flexShrink: 0,
                  background: 'rgba(255,255,255,0.12)',
                  borderRadius: 'var(--radius-sm)',
                  border: '0.5px solid rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <User size={16} className="text-white" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <p style={{ fontSize: 14, fontWeight: 500, color: '#fff', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {profile?.firstName && profile?.lastName
                    ? `${profile.firstName} ${profile.lastName}`
                    : user?.email?.split('@')[0] || 'Utilisateur'}
                </p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.email || ''}
                </p>
              </div>
            </div>

            <div className="mx-4 mb-4" style={{ height: '0.5px', background: 'rgba(255,255,255,0.1)' }} />
          </>
        )}

        {isEstimationActive ? (
          /* ── Estimation mode: vertical stepper ── */
          <>
            {/* Address + edit mode */}
            <div className="px-4 mb-3" style={{ paddingTop: 16 }}>
              {isEditMode && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'rgba(249,115,22,0.25)',
                  border: '0.5px solid rgba(249,115,22,0.5)',
                  borderRadius: 6,
                  padding: '5px 10px',
                  marginBottom: 6,
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#F97316', flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 500, color: '#FED7AA' }}>Mode modification</span>
                </div>
              )}
              {formAddress ? (
                <div className="flex items-start gap-1.5">
                  <MapPin size={11} color="rgba(255,255,255,0.4)" style={{ marginTop: 3, flexShrink: 0 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.85)', lineHeight: 1.3, wordBreak: 'break-word' }}>
                      {formAddress}
                    </span>
                    {(formPostcode || formCity) && (
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.3 }}>
                        {[formPostcode, formCity].filter(Boolean).join(' ')}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>
                  Adresse non saisie
                </span>
              )}
            </div>

            {/* Step list — scrollable */}
            <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-2">
              <div className="relative">
                {DETAILED_STEPS.map((step, index) => {
                  const isCompleted = completedSteps.includes(step.id);
                  const isCurrent = currentStep === step.id;
                  const isAI = aiSteps.includes(step.id);

                  return (
                    <button
                      key={step.id}
                      onClick={() => onStepClickRef?.current(step.id)}
                      className="relative flex items-center gap-2.5 w-full text-left"
                      style={{
                        padding: '5px 6px',
                        borderRadius: 'var(--radius-sm)',
                        background: isCurrent ? 'rgba(255,255,255,0.09)' : 'transparent',
                        border: '0.5px solid transparent',
                        cursor: 'pointer',
                      }}
                    >
                      {/* Circle — orange tint when AI step */}
                      <div style={{
                        width: 18, height: 18,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isCompleted
                          ? 'var(--gradient-accent)'
                          : isCurrent
                          ? 'white'
                          : isAI
                          ? 'rgba(249,115,22,0.25)'
                          : 'rgba(255,255,255,0.12)',
                        fontSize: 8,
                        fontWeight: 700,
                        color: isCompleted ? 'white' : isCurrent ? '#1E1148' : isAI ? '#FB923C' : 'rgba(255,255,255,0.4)',
                        position: 'relative',
                        zIndex: 1,
                        outline: isAI && !isCompleted && !isCurrent ? '1px solid rgba(249,115,22,0.4)' : 'none',
                      }}>
                        {isCompleted ? <Check size={9} strokeWidth={3} /> : index + 1}
                      </div>

                      {/* Label */}
                      <span style={{
                        fontSize: 12,
                        color: isCurrent ? '#ffffff' : isCompleted ? 'rgba(255,255,255,0.75)' : isAI ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.38)',
                        fontWeight: (isCurrent || isCompleted || isAI) ? 500 : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}>
                        {step.title}
                      </span>

                      {/* AI indicator — small sparkle for steps with pre-selected items */}
                      {isAI && !isCompleted && (
                        <Sparkles
                          size={9}
                          strokeWidth={2}
                          color="#F97316"
                          style={{ flexShrink: 0, opacity: 0.85 }}
                        />
                      )}

                      {index === 0 && isCompleted && (
                        <Lock size={11} color="rgba(255,255,255,0.3)" strokeWidth={2} style={{ flexShrink: 0 }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          /* ── Normal mode: nav items ── */
          <>
            <nav className="flex flex-col gap-1 px-3">
              <NavItem
                to={NAV_ITEMS[0].to}
                icon={NAV_ITEMS[0].icon}
                label={NAV_ITEMS[0].label}
                active={isActive(NAV_ITEMS[0].to)}
              />
              {/* SimulReno AI — icône orange distincte */}
              <Link
                to={AI_NAV_ITEM.to}
                className="flex items-center gap-3 px-3 py-2 text-sm transition-colors"
                style={{
                  borderRadius: 'var(--radius-sm)',
                  color: isActive(AI_NAV_ITEM.to) ? '#ffffff' : 'rgba(255,255,255,0.55)',
                  background: isActive(AI_NAV_ITEM.to) ? 'rgba(255,255,255,0.13)' : 'transparent',
                  border: isActive(AI_NAV_ITEM.to) ? '0.5px solid rgba(255,255,255,0.15)' : '0.5px solid transparent',
                }}
              >
                <Sparkles size={16} strokeWidth={1.75} color="#F97316" />
                {AI_NAV_ITEM.label}
                <span style={{ fontSize: 9, fontWeight: 500, padding: '2px 5px', borderRadius: 4, background: 'rgba(249,115,22,0.2)', color: '#FB923C', border: '0.5px solid rgba(249,115,22,0.3)', marginLeft: 6, verticalAlign: 'middle' }}>Beta</span>
              </Link>
              {NAV_ITEMS.slice(1).map((item) => (
                <NavItem
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  active={isActive(item.to)}
                />
              ))}
            </nav>

            <div className="mt-6 px-3">
              <p
                className="px-3 mb-2 text-xs font-medium tracking-widest uppercase"
                style={{ color: 'rgba(255,255,255,0.35)' }}
              >
                Compte
              </p>
              <nav className="flex flex-col gap-1">
                {ACCOUNT_ITEMS.map((item) => (
                  <NavItem
                    key={item.label}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    active={item.label === 'Profil'
                      ? location.pathname === '/profile'
                      : item.label === 'Facturation'
                      ? location.pathname === '/billing'
                      : false}
                  />
                ))}
                <a
                  href="mailto:support@simulreno.com"
                  className="flex items-center gap-3 px-3 py-2 text-sm transition-colors"
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    color: 'rgba(255,255,255,0.55)',
                    background: 'transparent',
                    border: '0.5px solid transparent',
                    textDecoration: 'none',
                  }}
                >
                  <Mail size={16} strokeWidth={1.75} />
                  Contact
                </a>
              </nav>
            </div>

            <div className="flex-1" />
          </>
        )}

        {/* Credits widget */}
        {!isEstimationActive && (
          isUnlimited ? (
            <div className="p-3 mx-3 mb-5" style={{
              background: 'rgba(249,115,22,0.15)',
              border: '0.5px solid rgba(249,115,22,0.3)',
              borderRadius: 'var(--radius-md)',
            }}>
              <Crown size={20} color="#F97316" strokeWidth={1.75} style={{ marginBottom: 6 }} />
              <p style={{ fontSize: 13, fontWeight: 500, color: '#fff', lineHeight: 1.3 }}>Plan Professionnel</p>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>Accès illimité</p>
            </div>
          ) : (
            <div className="p-3 mx-3 mb-5" style={{
              background: 'rgba(0,0,0,0.28)',
              border: '0.5px solid rgba(255,255,255,0.1)',
              borderRadius: 'var(--radius-md)',
            }}>
              <p className="text-xs font-medium tracking-widest uppercase mb-1" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Crédits
              </p>
              <p className="text-white font-bold text-3xl leading-none mb-0.5">{credits}</p>
              <p className="text-xs mb-3" style={{ color: 'rgba(255,255,255,0.45)' }}>estimations disponibles</p>
              <div className="w-full mb-3" style={{ height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 99 }}>
                <div style={{
                  height: '100%', width: `${barPercent}%`,
                  background: 'var(--gradient-accent)', borderRadius: 99, transition: 'width 0.4s ease',
                }} />
              </div>
              <Link
                to="/credits/purchase"
                className="flex items-center justify-center w-full text-white text-sm font-medium py-2"
                style={{ background: 'var(--gradient-accent)', borderRadius: 'var(--radius-sm)' }}
              >
                + Recharger
              </Link>
            </div>
          )
        )}

        {/* Sign out */}
        <button
          onClick={() => signOut()}
          className="flex items-center gap-2 px-5 py-3 text-sm w-full transition-colors"
          style={{
            color: 'rgba(255,255,255,0.4)',
            borderTop: '0.5px solid rgba(255,255,255,0.07)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
        >
          <LogOut size={14} strokeWidth={1.75} />
          Déconnexion
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
